import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useRecoilValue } from 'recoil';
import {
  addNewParent,
  clearClientProps,
} from '../../../redux/actions/petActions';
import { handleRequest } from '../../../redux/services/APIService';
import MessageModal from '../../../shared/components/Modals/MessageModal';
import MaskedInput from '../../../shared/components/inputs/Masked';
import localStorage from '../../../libs/storageHelper';
import { useFocusedUtilityEscape } from '../../../hooks/useFocusedUtilityEscape';
import { useInviteParentUtility } from './InviteParentUtility.hooks';
import { inviteParentUtilityStateAtom } from './InviteParentUtility.state';

const SIDEBAR_GAP = 16;
const VIEWPORT_MARGIN = 12;
/** Stable viewport top near Logout band; launcher position is ignored. */
const PREFERRED_TOP = 260;
const SUCCESS_NOTICE_MS = 3750;
const STATUS_NOTICE_MS = 3750;
const SIDEBAR_TRANSITION_FALLBACK_MS = 300;
const DRAG_THRESHOLD_PX = 5;

const EMPTY_MODAL = {
  show: false,
  type: 'ok',
  alertType: 'info',
  message: '',
  subMessage: '',
  okText: 'OK',
  cancelText: 'OK',
  action: null,
};

const INCOMPLETE_NUMBER_ERROR = 'Incomplete number';

const AUTOFILL_GUARD = {
  autoComplete: 'off',
  'data-bwignore': 'true',
  'data-lpignore': 'true',
  'data-1p-ignore': true,
};

function isOrdinaryPhoneErrorMessage(message) {
  if (!message) {
    return false;
  }
  return (
    message === INCOMPLETE_NUMBER_ERROR ||
    message === 'Please provide a valid phone number' ||
    message === 'Please provide your mobile phone'
  );
}

function formatInvitedDate(iso) {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatPendingLabel(invitedAt) {
  const formatted = formatInvitedDate(invitedAt);
  return formatted ? `Invited ${formatted} · Pending` : 'Pending';
}

function getLayoutMetrics() {
  const sidebar = document.querySelector('.sidebar');
  const sidebarRect = sidebar?.getBoundingClientRect();
  const sidebarRight = sidebarRect?.right ?? 240;
  const addPetLauncher = document.querySelector('[data-add-pet-launcher]');
  const launcherTitle = addPetLauncher?.querySelector('.sidebar__link-title');
  const anchorRect = (launcherTitle || addPetLauncher)?.getBoundingClientRect();
  const preferredTop =
    anchorRect != null ? Math.round(anchorRect.top) : PREFERRED_TOP;
  return {
    sidebarRight,
    preferredTop,
    preferredLeft: Math.round(sidebarRight + SIDEBAR_GAP),
  };
}

/**
 * Prefer sidebar + gap horizontally and fixed upper top.
 * When viewport fits sidebar + gap + panel + right margin, never overlap sidebar.
 * If too narrow for both, viewport visibility wins (may overlap sidebar).
 */
function resolvePanelPosition(panelRect) {
  const { sidebarRight, preferredTop, preferredLeft } = getLayoutMetrics();
  const width = panelRect?.width || 0;
  const height = panelRect?.height || 0;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const maxLeft = Math.max(
    VIEWPORT_MARGIN,
    viewportWidth - width - VIEWPORT_MARGIN,
  );
  const maxTop = Math.max(
    VIEWPORT_MARGIN,
    viewportHeight - height - VIEWPORT_MARGIN,
  );

  let left = Math.min(Math.max(preferredLeft, VIEWPORT_MARGIN), maxLeft);
  const top = Math.min(Math.max(preferredTop, VIEWPORT_MARGIN), maxTop);

  const sidebarSafeLeft = sidebarRight + SIDEBAR_GAP;
  const fitsBesideSidebar =
    width > 0 &&
    sidebarSafeLeft + width + VIEWPORT_MARGIN <= viewportWidth;

  if (fitsBesideSidebar) {
    left = Math.max(left, sidebarSafeLeft);
    left = Math.min(left, maxLeft);
  }

  return {
    top: Math.round(top),
    left: Math.round(left),
  };
}

/** Keep top/left in viewport; does not restore preferred/sidebar defaults. */
function clampToViewport(top, left, panelRect) {
  const width = panelRect?.width || 0;
  const height = panelRect?.height || 0;
  const maxLeft = Math.max(
    VIEWPORT_MARGIN,
    window.innerWidth - width - VIEWPORT_MARGIN,
  );
  const maxTop = Math.max(
    VIEWPORT_MARGIN,
    window.innerHeight - height - VIEWPORT_MARGIN,
  );
  return {
    top: Math.round(Math.min(Math.max(top, VIEWPORT_MARGIN), maxTop)),
    left: Math.round(Math.min(Math.max(left, VIEWPORT_MARGIN), maxLeft)),
  };
}

function focusMobileField() {
  requestAnimationFrame(() => {
    const el = document.querySelector(
      '.invite-parent-utility input[name="inviteMobile"]',
    );
    if (el && typeof el.focus === 'function') {
      el.focus();
    }
  });
}

export default function InviteParentUtility() {
  const dispatch = useDispatch();
  const { open } = useRecoilValue(inviteParentUtilityStateAtom);
  const { closeInviteParentUtility } = useInviteParentUtility();

  const [phoneEmail, setPhoneEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [statusNotice, setStatusNotice] = useState('');
  const [pendingInvite, setPendingInvite] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [position, setPosition] = useState({
    top: PREFERRED_TOP,
    left: 256,
  });
  const [modal, setModal] = useState(EMPTY_MODAL);
  const statusTimerRef = useRef(null);
  const panelRef = useRef(null);
  const sidebarTransitionCleanupRef = useRef(null);
  const userMovedRef = useRef(false);
  const positionRef = useRef({ top: PREFERRED_TOP, left: 256 });
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  positionRef.current = position;

  const clearStatusTimer = useCallback(() => {
    if (statusTimerRef.current) {
      clearTimeout(statusTimerRef.current);
      statusTimerRef.current = null;
    }
  }, []);

  const clearSidebarTransitionWatch = useCallback(() => {
    if (sidebarTransitionCleanupRef.current) {
      sidebarTransitionCleanupRef.current();
      sidebarTransitionCleanupRef.current = null;
    }
  }, []);

  const resetForm = useCallback(() => {
    setPhoneEmail('');
    setFirstName('');
    setLastName('');
    setMobileError('');
    setStatusNotice('');
    setPendingInvite(null);
    setSubmitting(false);
    setModal(EMPTY_MODAL);
    clearStatusTimer();
  }, [clearStatusTimer]);

  const applyPosition = useCallback(() => {
    const panelEl = panelRef.current;
    const rect = panelEl ? panelEl.getBoundingClientRect() : null;

    if (userMovedRef.current) {
      const current = positionRef.current;
      const next = rect
        ? clampToViewport(current.top, current.left, rect)
        : current;
      setPosition((prev) =>
        prev.top === next.top && prev.left === next.left ? prev : next,
      );
      return;
    }

    const next = rect
      ? resolvePanelPosition(rect)
      : (() => {
          const { preferredTop, preferredLeft } = getLayoutMetrics();
          return { top: preferredTop, left: preferredLeft };
        })();
    setPosition((prev) =>
      prev.top === next.top && prev.left === next.left ? prev : next,
    );
  }, []);

  const updatePosition = useCallback(() => {
    applyPosition();
    requestAnimationFrame(() => {
      requestAnimationFrame(applyPosition);
    });
  }, [applyPosition]);

  const watchSidebarWidthTransition = useCallback(() => {
    clearSidebarTransitionWatch();
    const sidebar = document.querySelector('.sidebar');
    if (!sidebar) {
      updatePosition();
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== sidebar || event.propertyName !== 'width') {
        return;
      }
      clearSidebarTransitionWatch();
      updatePosition();
    };

    const fallbackTimer = setTimeout(() => {
      clearSidebarTransitionWatch();
      updatePosition();
    }, SIDEBAR_TRANSITION_FALLBACK_MS);

    sidebar.addEventListener('transitionend', onTransitionEnd);
    sidebarTransitionCleanupRef.current = () => {
      clearTimeout(fallbackTimer);
      sidebar.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [clearSidebarTransitionWatch, updatePosition]);

  const closeUtility = useCallback(() => {
    userMovedRef.current = false;
    dragRef.current = null;
    setIsDragging(false);
    resetForm();
    closeInviteParentUtility();
  }, [closeInviteParentUtility, resetForm]);

  const closeModalOnly = useCallback(() => {
    setModal(EMPTY_MODAL);
  }, []);

  const showStatusNotice = useCallback(
    (message, durationMs = STATUS_NOTICE_MS) => {
      clearStatusTimer();
      setStatusNotice(message);
      statusTimerRef.current = setTimeout(() => {
        setStatusNotice('');
        statusTimerRef.current = null;
      }, durationMs);
    },
    [clearStatusTimer],
  );

  const showInform = useCallback((message, subMessage = '') => {
    setModal({
      show: true,
      type: 'ok',
      alertType: 'info',
      message,
      subMessage,
      okText: 'OK',
      cancelText: 'OK',
      action: null,
    });
  }, []);

  /** Clear fields, drop pending, stay open, in-panel acknowledgment. */
  const acknowledgeSentStayOpen = useCallback(
    (message) => {
      setModal(EMPTY_MODAL);
      setPendingInvite(null);
      setPhoneEmail('');
      setFirstName('');
      setLastName('');
      setMobileError('');
      showStatusNotice(message, SUCCESS_NOTICE_MS);
      focusMobileField();
    },
    [showStatusNotice],
  );

  const submitInvite = useCallback(
    async ({ addToCurrentAccount = false } = {}) => {
      const data = {
        parent: {
          phoneEmail,
          firstName,
          lastName,
          ...(addToCurrentAccount ? { addToCurrentAccount: true } : {}),
        },
        pets: [],
      };
      setSubmitting(true);
      const result = await dispatch(addNewParent(data));
      dispatch(clearClientProps());
      setSubmitting(false);

      if (result?.errorMessage && !result?.data?.status) {
        if (isOrdinaryPhoneErrorMessage(result.errorMessage)) {
          setMobileError(INCOMPLETE_NUMBER_ERROR);
          return;
        }
        showInform(result.errorMessage);
        return;
      }

      const status = result?.data?.status;
      const apiMessage = result?.data?.message || '';
      const parentData = result?.data?.data;
      const invitedAt = result?.data?.invitedAt || null;

      switch (status) {
        case 'parent-added-success':
        case 'parent-not-associated':
        case 'parent-not-associated-with-facility':
          acknowledgeSentStayOpen('Invite sent');
          break;
        case 'parent-already-invited':
          clearStatusTimer();
          setStatusNotice('');
          setPendingInvite({
            parentId: parentData?._id || null,
            invitedAt,
          });
          break;
        case 'parent-exist-facility-active':
          setPendingInvite(null);
          showStatusNotice('Already connected to this facility');
          break;
        case 'parent-exist-facility-not-exist':
          setModal({
            show: true,
            type: 'confirm',
            alertType: 'info',
            message:
              'The user associated with the Mobile Number you entered is already registered with Loyal.',
            subMessage:
              'Would you like to invite this registered Loyal user to be connected to your facility, including being able to use the Loyal mobile app to communicate with your facility?',
            okText: 'Yes',
            cancelText: 'No',
            action: 'reconnect',
          });
          break;
        case 'parent-exist-facility-inactive':
          showInform('This parent already exists.');
          break;
        default:
          showInform(apiMessage || 'This parent already exists');
          break;
      }
    },
    [
      acknowledgeSentStayOpen,
      clearStatusTimer,
      dispatch,
      firstName,
      lastName,
      phoneEmail,
      showInform,
      showStatusNotice,
    ],
  );

  const onReinvite = useCallback(async () => {
    if (!pendingInvite?.parentId || submitting || modal.show) {
      return;
    }
    const facility = localStorage.getFromStorage('loggedUser')?.user?.facility;
    setSubmitting(true);
    try {
      const results = await handleRequest('post', 'client/reinvite', true, {
        id: pendingInvite.parentId,
        facility,
      });
      const status = results?.data?.status;
      if (status === 'reinvite') {
        acknowledgeSentStayOpen('Re-invite sent');
        return;
      }
      if (status === 'reinvite-cooldown') {
        if (results?.data?.invitedAt) {
          setPendingInvite((prev) =>
            prev
              ? {
                  ...prev,
                  invitedAt: results.data.invitedAt,
                }
              : prev,
          );
        }
        showStatusNotice('Invite sent recently');
        return;
      }
      showInform(results?.data?.message || 'Unable to re-invite right now.');
    } catch (error) {
      const message =
        error?.response?.data?.errorMessage ||
        error?.message ||
        'Unable to re-invite right now.';
      showInform(message);
    } finally {
      setSubmitting(false);
    }
  }, [
    acknowledgeSentStayOpen,
    modal.show,
    pendingInvite,
    showInform,
    showStatusNotice,
    submitting,
  ]);

  const onInvite = useCallback(() => {
    if (modal.show || submitting) {
      return;
    }
    const digits = String(phoneEmail).replace(/\D/g, '');
    if (!digits) {
      setMobileError('Required field');
      return;
    }
    if (digits.length !== 10) {
      setMobileError(INCOMPLETE_NUMBER_ERROR);
      return;
    }
    setMobileError('');
    submitInvite();
  }, [modal.show, phoneEmail, submitInvite, submitting]);

  const onFieldEnter = useCallback(
    (e) => {
      if (e.key !== 'Enter') {
        return;
      }
      e.preventDefault();
      onInvite();
    },
    [onInvite],
  );

  const onModalSuccess = useCallback(() => {
    if (modal.action === 'reconnect') {
      setModal(EMPTY_MODAL);
      submitInvite({ addToCurrentAccount: true });
      return;
    }
    closeModalOnly();
  }, [closeModalOnly, modal.action, submitInvite]);

  const onTitlePointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) {
      return;
    }
    e.preventDefault();
    const target = e.currentTarget;
    if (typeof target.setPointerCapture === 'function') {
      target.setPointerCapture(e.pointerId);
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originTop: positionRef.current.top,
      originLeft: positionRef.current.left,
      moved: false,
    };
  }, []);

  const onTitlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) {
      return;
    }
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.moved = true;
      userMovedRef.current = true;
      setIsDragging(true);
    }
    const panelEl = panelRef.current;
    const rect = panelEl
      ? panelEl.getBoundingClientRect()
      : { width: 0, height: 0 };
    const next = clampToViewport(
      drag.originTop + dy,
      drag.originLeft + dx,
      rect,
    );
    setPosition(next);
  }, []);

  const endTitlePointerDrag = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) {
      return;
    }
    const target = e.currentTarget;
    if (
      typeof target.hasPointerCapture === 'function' &&
      target.hasPointerCapture(e.pointerId) &&
      typeof target.releasePointerCapture === 'function'
    ) {
      target.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    userMovedRef.current = false;
    dragRef.current = null;
    setIsDragging(false);
    resetForm();
    updatePosition();

    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);

    const sidebar = document.querySelector('.sidebar');
    const observer = sidebar
      ? new MutationObserver(() => {
          updatePosition();
          watchSidebarWidthTransition();
        })
      : null;
    if (sidebar && observer) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (observer) {
        observer.disconnect();
      }
      clearSidebarTransitionWatch();
    };
  }, [
    clearSidebarTransitionWatch,
    open,
    resetForm,
    updatePosition,
    watchSidebarWidthTransition,
  ]);

  useEffect(() => {
    if (!open || (!statusNotice && !pendingInvite)) {
      return undefined;
    }
    updatePosition();
    return undefined;
  }, [open, pendingInvite, statusNotice, updatePosition]);

useFocusedUtilityEscape({
  enabled: open,
  panelRef,
  onEscape: closeUtility,
});

  useEffect(
    () => () => {
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current);
      }
      clearSidebarTransitionWatch();
    },
    [clearSidebarTransitionWatch],
  );

  if (!open) {
    return null;
  }

  return createPortal(
    <>
      <div
        ref={panelRef}
        className="invite-parent-utility"
        style={{ top: position.top, left: position.left }}
      >
        <div className="invite-parent-utility__panel">
          <div
            className={
              'invite-parent-utility__title' +
              (isDragging ? ' invite-parent-utility__title--dragging' : '')
            }
            onPointerDown={onTitlePointerDown}
            onPointerMove={onTitlePointerMove}
            onPointerUp={endTitlePointerDrag}
            onPointerCancel={endTitlePointerDrag}
          >
            Invite Pet Parent
          </div>
          <form
            className="invite-parent-utility__grid"
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              onInvite();
            }}
          >
            <label className="invite-parent-utility__label invite-parent-utility__label--mobile">
              Mobile Number *
            </label>
            <label className="invite-parent-utility__label invite-parent-utility__label--first">
              First Name
            </label>
            <label className="invite-parent-utility__label invite-parent-utility__label--last">
              Last Name
            </label>
            <MaskedInput
              className="invite-parent-utility__input invite-parent-utility__input--mobile"
              name="inviteMobile"
              value={phoneEmail}
              onChange={(e) => {
                setPhoneEmail(e.target.value);
                if (mobileError) {
                  setMobileError('');
                }
                if (pendingInvite) {
                  setPendingInvite(null);
                }
                if (statusNotice) {
                  clearStatusTimer();
                  setStatusNotice('');
                }
              }}
              onKeyDown={onFieldEnter}
              autoFocus
              disabled={submitting}
              {...AUTOFILL_GUARD}
            />
            <input
              className="invite-parent-utility__input invite-parent-utility__input--first"
              name="inviteGiven"
              type="text"
              value={firstName}
              maxLength={50}
              onChange={(e) => setFirstName(e.target.value)}
              onKeyDown={onFieldEnter}
              disabled={submitting}
              {...AUTOFILL_GUARD}
            />
            <input
              className="invite-parent-utility__input invite-parent-utility__input--last"
              name="inviteFamily"
              type="text"
              value={lastName}
              maxLength={50}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={onFieldEnter}
              disabled={submitting}
              {...AUTOFILL_GUARD}
            />
            <div className="invite-parent-utility__error">{mobileError}</div>
            <div className="invite-parent-utility__actions">
              <button
                type="submit"
                className="invite-parent-utility__invite"
                disabled={submitting}
              >
                Invite
              </button>
              <button
                type="button"
                className="invite-parent-utility__cancel"
                onClick={closeUtility}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
          {statusNotice ? (
            <p className="invite-parent-utility__status-hint">{statusNotice}</p>
          ) : null}
        </div>
        {pendingInvite ? (
          <div className="invite-parent-utility__response">
            <div className="invite-parent-utility__pending">
              <p className="invite-parent-utility__pending-text">
                {formatPendingLabel(pendingInvite.invitedAt)}
              </p>
              <button
                type="button"
                className="invite-parent-utility__reinvite"
                onClick={onReinvite}
                disabled={submitting || !pendingInvite.parentId}
              >
                Re-invite
              </button>
            </div>
          </div>
        ) : null}
      </div>
      <MessageModal
        show={modal.show}
        type={modal.type}
        alertType={modal.alertType}
        footer
        backdrop="static"
        okText={modal.okText}
        cancelText={modal.cancelText}
        onSuccess={onModalSuccess}
        onClose={closeModalOnly}
        message={modal.message}
        subMessage={modal.subMessage}
      />
    </>,
    document.body,
  );
}
